import { ArticlePageHero as UIArticlePageHero } from "@business-finland/wif-ui-lib/dist/ArticlePageHero";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";

import { Viewport } from "../../../constants/app";
import usePageContent from "../../../contexts/PageContentContext";
import useImageDataHandler from "../../../hooks/useImageDataHandler";
import { getAssetPath } from "../../../util/magnolia";
import useSavedItems from "../../../contexts/SaveItemsToStorageContext";
import { ISavedItem } from "../../../types/saved-items";

const HtmlParser = dynamic(() => import("../../../components/HtmlParser"));

type IICons = "true" | "false";

export type IAsset =
  | { field: "image"; image: Wif.Mgnl.Image | string; imageAltText?: string }
  | { field: "video"; video: Wif.Mgnl.Video; videoAltText?: string };

export interface IArticlePageHero extends Wif.Mgnl.Node {
  heading: string;
  leadText?: string;
  asset: IAsset;
  imageReference?: string;
  quote: Wif.Mgnl.Node<IQuote>;
  icons: IICons;
  description?: string;
}

export interface IQuote {
  field: string;
  title: string;
  source?: string;
  ranking?: string;
}

export function ArticlePageHero(props: IArticlePageHero) {
  const {
    heading,
    leadText,
    asset,
    imageReference,
    quote,
    icons,
    description,
  } = props;
  const { isPreview } = useRouter();
  const imageDataHandler = useImageDataHandler();
  const { toggleItemInLocalStorage, isSaved } = useSavedItems();
  const currentPage = usePageContent();

  const generateHeroSource = () => {
    if (asset.field === "image") {
      const { image } = asset;

      let credits = "";

      if (typeof image !== "string") {
        credits = image?.metadata?.rights ?? "";
      }

      const imageData = imageDataHandler(image, { maxViewport: Viewport.md });
      return {
        displaySource: { ...imageData, imageSrc: imageData.src },
        altText: asset.imageAltText || "",
        mimeType: undefined,
        credits,
      };
    }

    return {
      displaySource: getAssetPath(asset.video, isPreview),
      imageLoadHandler: undefined,
      altText: asset.videoAltText || "",
      mimeType: asset.video.metadata?.mimeType,
    };
  };

  const handleSaveItem = () => {
    const id = currentPage["@id"];
    const path = currentPage["@path"];
    const cleanedDescription = description?.replace(/[<p></p>]/g, "");
    const introImage = currentPage.introductionImage;

    if (!introImage) return null;

    const newSaveItemObj: ISavedItem = {
      id: id,
      title: heading,
      description: cleanedDescription!,
      pageLink: path,
      image: introImage,
      type: "masonryCard",
    };

    toggleItemInLocalStorage(newSaveItemObj);
  };

  const descriptionOrUndefined = description?.trim() ? (
    <HtmlParser html={description} />
  ) : undefined;

  return (
    <UIArticlePageHero
      assetType={asset.field}
      heading={heading}
      leadText={leadText?.trim()}
      heroSource={generateHeroSource()}
      imageReference={imageReference}
      quote={getQuoteFromQuoteNode(quote)}
      hasIcons={icons === "true"}
      description={descriptionOrUndefined}
      isSaved={isSaved(currentPage?.["@id"]!)}
      onSave={handleSaveItem}
    />
  );
}

function getQuoteFromQuoteNode(
  node: Wif.Mgnl.Node<IQuote>
): IQuote | undefined {
  if (node.field === "none") return undefined;

  return {
    field: node.field,
    title: node.title,
    source: node.source,
    ranking: node.ranking,
  };
}
